"use client";

import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { DeleteConfirmationAlert } from "@/components/DeleteConfirmationAlert";
import { useDeleteConfirmation } from "@/hooks/use-delete-confirmation";
import { OffersService } from "@/services/OffersService";
import type { OffersOutput, Pagination } from "@/interfaces";
import { numberToMoney } from "@/util/moneyConversion";
import { format } from "date-fns";
import { Plus, Trash, Edit, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

const PaginationControls = ({
  pagination,
  onPageChange,
  onPageSizeChange,
}: {
  pagination: Pagination;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}) => {
  const { page, pageSize, total, pages } = pagination;
  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-2">
      <div className="text-sm text-gray-500">
        Mostrando {total > 0 ? startItem : 0} a {endItem} de {total} ofertas
      </div>

      <div className="flex items-center gap-2">
        <div className="flex items-center mr-4">
          <span className="text-sm text-gray-500 mr-2">Itens por página:</span>
          <Select
            value={pageSize.toString()}
            onValueChange={(value) => onPageSizeChange(Number(value))}
          >
            <SelectTrigger className="h-8 w-[70px]">
              <SelectValue placeholder={pageSize.toString()} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="5">5</SelectItem>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="flex items-center gap-1">
            {Array.from({ length: Math.max(1, Math.min(pages, 5)) }, (_, i) => {
              let pageNumber: number;
              if (pages <= 5) {
                pageNumber = i + 1;
              } else if (page <= 3) {
                pageNumber = i + 1;
              } else if (page >= pages - 2) {
                pageNumber = pages - 4 + i;
              } else {
                pageNumber = page - 2 + i;
              }

              return (
                <Button
                  key={pageNumber}
                  variant={page === pageNumber ? "default" : "outline"}
                  size="icon"
                  className={cn(
                    "h-8 w-8",
                    page === pageNumber && "bg-[#EE7B11] hover:bg-[#d06a0a]"
                  )}
                  onClick={() => onPageChange(pageNumber)}
                >
                  {pageNumber}
                </Button>
              );
            })}
          </div>

          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= pages}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function Offers() {
  const offersService = useMemo(() => new OffersService(), []);
  const navigate = useNavigate();
  const [offers, setOffers] = useState<OffersOutput[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    pageSize: 10,
    pages: 0,
    page: 1,
    links: { self: "" },
  });

  const { isOpen, itemToDelete, open, close } = useDeleteConfirmation<string>();

  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        page: pagination.page,
        pageSize: pagination.pageSize,
      };
      if (search) params.query = search;

      const { data, pagination: pag } = await offersService.findAll({
        ...params,
        expand: ["file"],
      });
      setOffers(data);
      setPagination(pag);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, search, offersService]);

  useEffect(() => {
    fetchOffers();
  }, [pagination.page, pagination.pageSize, search, fetchOffers]);

  const handleSearch = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      const value = (e.target as HTMLInputElement).value;
      setSearch(value);
      setPagination((p) => ({ ...p, page: 1 }));
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await offersService.delete(itemToDelete);
      close();
      fetchOffers();
    } catch {
      /* ignore */
    }
  };

  const handleEdit = (offer: OffersOutput) => {
    navigate(`/ofertas/${offer.id}/editar`);
  };

  const handleNew = () => {
    navigate("/ofertas/novo");
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <Card className="overflow-hidden border-none shadow-md">
        <CardHeader className="bg-gradient-to-r from-[#EE7B11] to-[#f59642] text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Plus className="h-6 w-6" />
              <CardTitle className="text-xl font-semibold">Ofertas</CardTitle>
            </div>
            <Button
              onClick={handleNew}
              size="sm"
              className="bg-white text-[#EE7B11] hover:bg-gray-100 font-medium"
            >
              <Plus className="h-4 w-4 mr-2" /> Nova Oferta
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <Input
              placeholder="Buscar por nome"
              className="w-full md:max-w-xs h-10"
              onKeyDown={handleSearch}
            />
          </div>
          {loading ? (
            <div className="flex justify-center p-8">
              <LoadingSpinner />
            </div>
          ) : offers.length > 0 ? (
            <div className="space-y-4">
              <div className="border rounded-lg">
                <div className="w-full overflow-x-auto">
                  <Table className="min-w-[960px]">
                    <TableHeader>
                      <TableRow className="bg-gray-50/50">
                        <TableHead className="font-semibold">Nome</TableHead>
                        <TableHead className="font-semibold">Descrição</TableHead>
                        <TableHead className="font-semibold">Produtos</TableHead>
                        <TableHead className="text-right font-semibold">
                          Preço
                        </TableHead>
                        <TableHead className="text-right font-semibold">
                          Margem
                        </TableHead>
                        <TableHead className="font-semibold">Criado em</TableHead>
                        <TableHead className="font-semibold">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {offers.map((offer) => {
                        const totalCost = offer.products.reduce(
                          (sum, p) => sum + (p.unitCost || 0) * (p.quantity || 1),
                          0
                        );
                        const unitPrice = offer.unitPrice || 0;
                        const margin = unitPrice - totalCost;
                        const marginPercentage =
                          unitPrice > 0 ? (margin / unitPrice) * 100 : 0;

                        return (
                          <TableRow
                            key={offer.id}
                            className="hover:bg-gray-50/50"
                          >
                            <TableCell className="font-medium">
                              {offer.name}
                            </TableCell>
                            <TableCell className="max-w-sm">
                              <div
                                className="truncate"
                                title={offer.description}
                                dangerouslySetInnerHTML={{
                                  __html: offer.description || "",
                                }}
                              />
                            </TableCell>
                            <TableCell className="space-y-1">
                              {offer.products.map((p, index) => (
                                <div
                                  key={`${p.productId}-${index}`}
                                  className="text-sm"
                                >
                                  <span className="font-medium">{p.name}</span>
                                  <span className="text-gray-500 ml-2">
                                    ({numberToMoney(p.unitPrice)})
                                  </span>
                                  {p.quantity && p.quantity > 1 && (
                                    <span className="text-orange-600 ml-1 font-medium">
                                      x{p.quantity}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {numberToMoney(offer.unitPrice || 0)}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex flex-col items-end gap-1">
                                <span
                                  className={`font-medium text-sm ${
                                    margin >= 0
                                      ? "text-green-600"
                                      : "text-red-600"
                                  }`}
                                >
                                  {marginPercentage.toFixed(1)}%
                                </span>
                                <span
                                  className={`text-xs ${
                                    margin >= 0
                                      ? "text-green-500"
                                      : "text-red-500"
                                  }`}
                                >
                                  {numberToMoney(margin)}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-gray-600">
                              {format(new Date(offer.createdAt), "dd/MM/yyyy")}
                            </TableCell>
                            <TableCell>
                              <div className="flex justify-end gap-2">
                                <Button
                                variant="outline"
                                size="sm"
                                className="border-[#EE7B11] text-[#EE7B11] hover:bg-[#EE7B11]/10"
                                onClick={() => handleEdit(offer)}
                              >
                                <Edit className="h-4 w-4 mr-1" />
                                Editar
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="border-red-500 text-red-500 hover:bg-red-50"
                                onClick={() => open(offer.id)}
                              >
                                <Trash className="h-4 w-4 mr-1" />
                                Excluir
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
              <PaginationControls
                pagination={pagination}
                onPageChange={(page) => setPagination((p) => ({ ...p, page }))}
                onPageSizeChange={(pageSize) =>
                  setPagination((p) => ({ ...p, pageSize, page: 1 }))
                }
              />
            </div>
          ) : (
            <div className="text-center py-12 border rounded-lg bg-gray-50/30">
              <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Plus className="h-6 w-6 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold mb-2 text-gray-900">
                Nenhuma oferta encontrada
              </h3>
              <p className="text-gray-600 mb-6 max-w-sm mx-auto">
                {search
                  ? "Não encontramos ofertas com os filtros aplicados"
                  : "Você ainda não possui ofertas cadastradas. Comece criando sua primeira oferta."}
              </p>
              <Button
                onClick={handleNew}
                className="bg-[#EE7B11] hover:bg-[#d06a0a] text-white"
              >
                <Plus className="h-4 w-4 mr-2" /> Nova Oferta
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
      <DeleteConfirmationAlert
        isOpen={isOpen}
        onClose={close}
        onConfirm={handleDelete}
        itemName={offers.find((o) => o.id === itemToDelete)?.name}
        message="Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita."
      />
    </div>
  );
}
